/**
 * Page "Boîte à suggestions" — portail étudiant.
 * Permet de poster un avis / suggestion, anonymement ou en s'identifiant.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Inbox, Send, EyeOff, User, CheckCircle, AlertCircle, Loader2,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi } from '../services/portailApi';

const CATEGORIES = [
  { v: 'cours',          label: 'Cours / Pédagogie' },
  { v: 'formateur',      label: 'Un formateur' },
  { v: 'fonctionnement', label: "Fonctionnement de l'école" },
  { v: 'administration', label: 'Administration / Scolarité' },
  { v: 'infrastructure', label: 'Infrastructure / Locaux' },
  { v: 'autre',          label: 'Autre' },
];

export default function Suggestion() {
  const navigate = useNavigate();
  const [categorie, setCategorie] = useState('cours');
  const [titre, setTitre] = useState('');
  const [contenu, setContenu] = useState('');
  const [anonyme, setAnonyme] = useState(false);
  const [cibleFormateur, setCibleFormateur] = useState('');
  const [cibleMatiere, setCibleMatiere] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (contenu.trim().length < 5) {
      setError('Votre message doit faire au moins 5 caractères.');
      return;
    }
    setSubmitting(true);
    try {
      await portailApi.creerSuggestion({
        categorie, titre: titre.trim() || undefined,
        contenu: contenu.trim(), anonyme,
        cible_formateur_nom: cibleFormateur.trim() || undefined,
        cible_matiere: cibleMatiere.trim() || undefined,
      });
      setSuccess(true);
    } catch (e: any) {
      setError(e?.message || 'Impossible d\'envoyer votre suggestion.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <PortailLayout showHeader title="Suggestion envoyée">
        <div className="bg-gray-900 border border-emerald-700/50 rounded-xl p-6 text-center">
          <CheckCircle size={48} className="mx-auto mb-3 text-emerald-400" />
          <h2 className="text-lg font-bold text-gray-100 mb-2">Merci pour votre retour !</h2>
          <p className="text-sm text-gray-400 mb-5">
            Votre {anonyme ? 'suggestion anonyme' : 'suggestion'} a bien été enregistrée.
            L'administration en prendra connaissance prochainement.
          </p>
          <div className="flex gap-2 justify-center">
            <button onClick={() => {
              setSuccess(false); setTitre(''); setContenu('');
              setCibleFormateur(''); setCibleMatiere('');
            }}
              className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-gray-100 rounded-lg">
              Envoyer une autre
            </button>
            <button onClick={() => navigate('/portail/accueil')}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
              Retour à l'accueil
            </button>
          </div>
        </div>
      </PortailLayout>
    );
  }

  const showCibleFormateur = categorie === 'formateur' || categorie === 'cours';

  return (
    <PortailLayout showHeader title="Boîte à suggestions">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-blue-900/20 border border-blue-800/50 rounded-xl p-3 text-sm text-blue-200 flex items-start gap-2">
          <Inbox size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-0.5">Donnez votre avis</div>
            <p className="text-xs text-blue-300/80">
              Vos retours sont précieux. Seule l'administration y a accès. Vous pouvez choisir de rester anonyme.
            </p>
          </div>
        </div>

        {/* Catégorie */}
        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wider">
            Catégorie
          </label>
          <div className="grid grid-cols-2 gap-1.5">
            {CATEGORIES.map(c => (
              <button key={c.v} type="button" onClick={() => setCategorie(c.v)}
                className={`px-2.5 py-2 text-xs font-medium rounded-lg border text-left ${
                  categorie === c.v
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-gray-900 text-gray-300 border-gray-800 hover:border-gray-600'
                }`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Cible (si pertinent) */}
        {showCibleFormateur && (
          <div className="grid grid-cols-1 gap-3 bg-gray-900 border border-gray-800 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
              Cible (optionnel)
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Nom du formateur</label>
              <input value={cibleFormateur} onChange={(e) => setCibleFormateur(e.target.value)}
                placeholder="Ex. M. Mbemba" maxLength={200}
                className="w-full px-3 py-2 text-sm bg-gray-950 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Matière concernée</label>
              <input value={cibleMatiere} onChange={(e) => setCibleMatiere(e.target.value)}
                placeholder="Ex. Algorithmique" maxLength={200}
                className="w-full px-3 py-2 text-sm bg-gray-950 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500" />
            </div>
          </div>
        )}

        {/* Titre */}
        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wider">
            Titre <span className="font-normal text-gray-500 normal-case">(optionnel)</span>
          </label>
          <input value={titre} onChange={(e) => setTitre(e.target.value)}
            maxLength={200} placeholder="Résumé en quelques mots"
            className="w-full px-3 py-2 text-sm bg-gray-900 border border-gray-800 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500" />
        </div>

        {/* Contenu */}
        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-2 uppercase tracking-wider">
            Votre message <span className="text-red-400">*</span>
          </label>
          <textarea value={contenu} onChange={(e) => setContenu(e.target.value)}
            rows={6} maxLength={5000} required
            placeholder="Partagez ici votre avis, suggestion, critique constructive ou idée d'amélioration."
            className="w-full px-3 py-2 text-sm bg-gray-900 border border-gray-800 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 resize-none" />
          <div className="text-[10px] text-gray-500 text-right mt-1">{contenu.length} / 5000</div>
        </div>

        {/* Anonyme toggle */}
        <label className="flex items-start gap-3 p-3 bg-gray-900 border border-gray-800 rounded-lg cursor-pointer hover:border-gray-700">
          <input type="checkbox" checked={anonyme} onChange={(e) => setAnonyme(e.target.checked)}
            className="mt-0.5 accent-amber-500" />
          <div className="flex-1">
            <div className="text-sm text-gray-200 font-medium inline-flex items-center gap-1.5">
              {anonyme ? <EyeOff size={14} /> : <User size={14} />}
              {anonyme ? 'Suggestion anonyme' : 'Joindre mon nom'}
            </div>
            <p className="text-[11px] text-gray-400 mt-1">
              {anonyme
                ? "Aucune info personnelle ne sera transmise. L'admin verra seulement votre pôle pour les statistiques."
                : "Votre nom, matricule et classe seront visibles uniquement par l'administration."}
            </p>
          </div>
        </label>

        {error && (
          <div className="bg-red-900/30 border border-red-800 text-red-200 rounded-lg p-3 text-sm flex items-start gap-2">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <button type="submit" disabled={submitting || contenu.trim().length < 5}
          className="w-full px-4 py-3 text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg inline-flex items-center justify-center gap-2">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {submitting ? 'Envoi en cours…' : 'Envoyer'}
        </button>
      </form>
    </PortailLayout>
  );
}
